// Generic resource-share service: one hook set parameterized by resource
// type over the per-resource share routes. Adding a share-aware resource =
// adding a CONFIG entry.
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import type {
  GroupShare,
  ShareCoverageResponse,
  ShareGrant,
  ShareLevel,
  ShareResourceType,
  SharesResponse,
  ShareVisibility,
  UserShare,
} from "./types";

interface ResourceShareConfig {
  basePath: (id: number) => string;
  sharesKey: (id: number | null) => [string, number | null];
  /** Everything to refresh after ANY share mutation on this resource type. */
  invalidateKeys: (id: number) => unknown[][];
}

const CONFIG: Record<ShareResourceType, ResourceShareConfig> = {
  dataset: {
    basePath: (id) => `${nuclearn_api_url}/datasets/${id}`,
    sharesKey: (id) => ["dataset-shares", id],
    // The grid is keyed ["datasetList", …] (capital L); the modals use
    // ["datasetlist"]; the viewer uses ["dataset", id]. Invalidate all so the
    // Public/Private chip + access reflect a visibility/share change
    // immediately.
    invalidateKeys: (id) => [
      ["dataset-shares", id],
      ["datasetList"],
      ["datasetlist"],
      ["dataset", id],
    ],
  },
  datasetFolder: {
    basePath: (id) => `${nuclearn_api_url}/dataset-folders/${id}`,
    sharesKey: (id) => ["dataset-folder-shares", id],
    // Folder shares surface on the folder card/viewer, the dataset lists, AND
    // every contained dataset's share dialog (datasets derive the folder's
    // grants), so ["dataset-shares"] is an id-less prefix matching them all.
    invalidateKeys: (id) => [
      ["dataset-folder-shares", id],
      ["dataset-shares"],
      ["datasetFolder", id],
      ["datasetFolders"],
      ["datasetList"],
      ["datasetlist"],
    ],
  },
  agent: {
    basePath: (id) => `${nuclearn_api_url}/chat/config/${id}`,
    sharesKey: (id) => ["agent-shares", id],
    // Every persona read carries my_access and visibility_level: the gallery
    // list and by-slug reads share the ["atomAssistConfig", …] prefix, the
    // by-id read its own, and the trending/starred sections theirs.
    invalidateKeys: (id) => [
      ["agent-shares", id],
      ["agent-share-coverage", id],
      ["atomAssistConfig"],
      ["atomAssistConfigById"],
      ["trendingPersonas"],
      ["personaStarredBy"],
    ],
  },
};

const invalidate = (
  queryClient: ReturnType<typeof useQueryClient>,
  resourceType: ShareResourceType,
  resourceId: number,
) => {
  for (const key of CONFIG[resourceType].invalidateKeys(resourceId)) {
    queryClient.invalidateQueries(key);
  }
};

const getShares = (
  resourceType: ShareResourceType,
  id: number,
): Promise<SharesResponse> =>
  axios
    .get(`${CONFIG[resourceType].basePath(id)}/shares`)
    .then((res) => res.data);

export const useResourceShares = (
  resourceType: ShareResourceType,
  id: number | null,
  enabled = true,
) =>
  useQuery(
    CONFIG[resourceType].sharesKey(id),
    () => getShares(resourceType, id!),
    {
      enabled: enabled && id !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );

export const getShareCoverage = (
  resourceType: ShareResourceType,
  id: number,
): Promise<ShareCoverageResponse> =>
  axios
    .get(`${CONFIG[resourceType].basePath(id)}/shares/coverage`)
    .then((res) => res.data);

/**
 * Coverage of the resource's attached datasets across its share roster,
 * computed by the backend at request time. Never served stale: every open
 * and every share change refetches.
 */
export const useResourceShareCoverage = (
  resourceType: ShareResourceType,
  id: number | null,
  enabled = true,
) =>
  useQuery(
    [`${resourceType}-share-coverage`, id],
    () => getShareCoverage(resourceType, id!),
    {
      enabled: enabled && id !== null,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      staleTime: 0,
      cacheTime: 0,
    },
  );

export const useAddResourceUserShare = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      data,
    }: {
      resourceId: number;
      data: { user_id: number; permission_level: ShareLevel };
    }): Promise<UserShare> =>
      axios
        .post(`${CONFIG[resourceType].basePath(resourceId)}/shares/user`, data)
        .then((res) => res.data),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

export const useAddResourceGroupShare = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      data,
    }: {
      resourceId: number;
      data: { group_id: number; permission_level: ShareLevel };
    }): Promise<GroupShare> =>
      axios
        .post(`${CONFIG[resourceType].basePath(resourceId)}/shares/group`, data)
        .then((res) => res.data),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

export const useRemoveResourceUserShare = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      userId,
    }: {
      resourceId: number;
      userId: number;
    }): Promise<void> =>
      axios.delete(
        `${CONFIG[resourceType].basePath(resourceId)}/shares/user/${userId}`,
      ),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

export const useRemoveResourceGroupShare = (
  resourceType: ShareResourceType,
) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      groupId,
    }: {
      resourceId: number;
      groupId: number;
    }): Promise<void> =>
      axios.delete(
        `${CONFIG[resourceType].basePath(resourceId)}/shares/group/${groupId}`,
      ),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

export const useSetResourceVisibility = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      visibility,
    }: {
      resourceId: number;
      visibility: ShareVisibility;
    }): Promise<void> =>
      axios.put(
        `${CONFIG[resourceType].basePath(resourceId)}/shares/visibility`,
        { visibility_level: visibility },
      ),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

export const useTransferResourceOwner = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      resourceId,
      newOwnerUserId,
    }: {
      resourceId: number;
      newOwnerUserId: number;
    }): Promise<void> =>
      axios.patch(`${CONFIG[resourceType].basePath(resourceId)}/owner`, {
        new_owner_user_id: newOwnerUserId,
      }),
    {
      onSuccess: (_d, v) => invalidate(queryClient, resourceType, v.resourceId),
    },
  );
};

/**
 * Apply grants one request at a time, so a failure is attributable to a
 * recipient. Never throws: the caller gets the grants that did not land and
 * decides how to surface them. Invalidates the resource's keys once at the
 * end, so its lists do not refetch per grant. `onProgress` gets the number of
 * grants attempted so far after each request.
 */
export const useApplyShareGrants = (resourceType: ShareResourceType) => {
  const queryClient = useQueryClient();
  return async <T extends ShareGrant>(
    resourceId: number,
    grants: T[],
    onProgress?: (done: number) => void,
  ): Promise<{ failed: T[] }> => {
    const failed: T[] = [];
    for (const [index, grant] of grants.entries()) {
      const path = `${CONFIG[resourceType].basePath(resourceId)}/shares/${grant.kind}`;
      const data =
        grant.kind === "user"
          ? { user_id: grant.id, permission_level: grant.level }
          : { group_id: grant.id, permission_level: grant.level };
      try {
        await axios.post(path, data);
      } catch {
        failed.push(grant);
      }
      onProgress?.(index + 1);
    }
    if (grants.length > 0) {
      invalidate(queryClient, resourceType, resourceId);
    }
    return { failed };
  };
};
